/* eslint-disable prettier/prettier */

import { apiService } from './api.service';
import { ApiResponse } from '../types';

/**
 * Goal Milestone Interface
 */
export interface GoalMilestone {
	id?: string;
	goalid?: string;

	title: string;
	description?: string;

	orderindex: number;

	targetdate?: string;

	measurementvalue?: number;

	createdat?: string;
	updatedat?: string;
}

/**
 * Goal Interface
 */
export interface Goal {
	id: string;

	userid: string;

	createdby: string;

	createdbyname?: string;

	programid?: string;

	programtitle?: string;

	istemplate: boolean;

	templateid?: string;

	title: string;

	description?: string;

	goaltype: string;

	category?: string;

	goaltypeid?: string;

	measurementtype?: string;

	targetvalue?: number;

	currentvalue?: number;

	progress?: number;

	unit?: string;

	startdate?: string;

	targetdate?: string;

	completedat?: string;

	priority?: 'low' | 'medium' | 'high';

	status?: string;

	milestones?: GoalMilestone[];

	createdat?: string;

	updatedat?: string;
}

/**
 * Create Goal Payload
 */
export interface CreateGoalPayload {
	userid: string;

	programid?: string;

	istemplate?: boolean;

	title?: string;

	description?: string;

	goaltype?: string;

	category?: string;

	goaltypeid?: string;

	measurementtype?:
		| 'numeric'
		| 'boolean'
		| 'percentage'
		| 'rating'
		| 'currency'
		| 'custom';

	targetvalue?: number;

	unit?: string;

	startdate?: string;

	targetdate?: string;

	priority?: 'low' | 'medium' | 'high';

	milestones?: GoalMilestone[];
}

/**
 * Update Goal Payload
 */
export interface UpdateGoalPayload {
	title?: string;

	description?: string | null;

	goaltype?: string;

	category?: string | null;

	measurementtype?:
		| 'numeric'
		| 'boolean'
		| 'percentage'
		| 'rating'
		| 'currency'
		| 'custom';

	targetvalue?: number | null;

	unit?: string | null;

	startdate?: string | null;

	targetdate?: string | null;

	priority?: 'low' | 'medium' | 'high';

	status?: 'active' | 'completed' | 'paused' | 'cancelled';

	goaltypeid?: string | null;
}

/**
 * Goal Filters
 */
export interface GoalFilters {
	userid?: string;

	programid?: string;

	istemplate?: boolean;

	status?: string;

	createdby?: string;

	page?: number;

	limit?: number;
}

/**
 * Goal List Response
 */
export interface GoalListResponse {
	goals: Goal[];

	total: number;

	page: number;

	limit: number;
}

/**
 * Update Goal Progress Payload
 */
export interface UpdateGoalProgressPayload {
	currentvalue: number;

	notes?: string;
}

/**
 * Goal Service
 * Handles all Goal APIs
 */
class GoalService {
	/**
	 * Fetch Goals
	 *
	 * GET /api/goal
	 */
	async getGoals(
		filters?: GoalFilters
	): Promise<ApiResponse<GoalListResponse>> {
		try {
			const response = await apiService.get<GoalListResponse>(
				'/goal',
				filters
			);

			console.log('Fetch Goals Response From Service:', response);

			return response;
		} catch (error: any) {
			console.error('Error fetching goals:', error);

			throw new Error(
				error?.response?.data?.message || 'Failed to fetch goals'
			);
		}
	}

	/**
	 * Fetch Goal By ID
	 *
	 * GET /api/goal/:id
	 */
	async getGoalById(goalId: string): Promise<ApiResponse<Goal>> {
		try {
			const response = await apiService.get<Goal>(`/goal/${goalId}`);

			return response;
		} catch (error: any) {
			console.error('Error fetching goal:', error);

			throw new Error(
				error?.response?.data?.message || 'Failed to fetch goal'
			);
		}
	}

	/**
	 * Create Goal
	 *
	 * POST /api/goal
	 */
	async createGoal(payload: CreateGoalPayload): Promise<ApiResponse<Goal>> {
		try {
			const response = await apiService.post<Goal>('/goal', payload);

			return response;
		} catch (error: any) {
			console.error('Error creating goal:', error);

			throw new Error(
				error?.response?.data?.message || 'Failed to create goal'
			);
		}
	}

	/**
	 * Update Goal
	 *
	 * PATCH /api/goal/:id
	 */
	async updateGoal(
		goalId: string,
		payload: UpdateGoalPayload
	): Promise<ApiResponse<Goal>> {
		try {
			const response = await apiService.patch<Goal>(
				`/goal/${goalId}`,
				payload
			);

			return response;
		} catch (error: any) {
			console.error('Error updating goal:', error);

			throw new Error(
				error?.response?.data?.message || 'Failed to update goal'
			);
		}
	}

	/**
	 * Assign Template Goal To Program
	 *
	 * POST /api/goal/:goalId/assign/:programId
	 */
	async assignTemplateGoalToProgram(
		goalId: string,
		programId: string
	): Promise<ApiResponse<{ assigned: number }>> {
		try {
			const response = await apiService.post<{ assigned: number }>(
				`/goal/${goalId}/assign/${programId}`
			);

			return response;
		} catch (error: any) {
			console.error('Error assigning template goal:', error);

			throw new Error(
				error?.response?.data?.message ||
					'Failed to assign template goal'
			);
		}
	}

	/**
	 * Update Goal Progress
	 *
	 * PATCH /goal/:id/progress
	 */
	async updateGoalProgress(
		goalId: string,
		payload: UpdateGoalProgressPayload
	): Promise<ApiResponse<Goal>> {
		try {
			const response = await apiService.patch<Goal>(
				`/goal/${goalId}/progress`,
				payload
			);

			return response;
		} catch (error: any) {
			console.error('Error updating goal progress:', error);

			throw new Error(
				error?.response?.data?.message ||
					'Failed to update goal progress'
			);
		}
	}

	/**
	 * Delete Goal
	 *
	 * DELETE /api/goal/:id
	 */
	async deleteGoal(goalId: string): Promise<ApiResponse<void>> {
		try {
			const response = await apiService.delete<void>(`/goal/${goalId}`);

			return response;
		} catch (error: any) {
			console.error('Error deleting goal:', error);

			throw new Error(
				error?.response?.data?.message || 'Failed to delete goal'
			);
		}
	}
}

/**
 * Singleton Export
 */
export const goalService = new GoalService();
